<template>
  <div class="reveal-content">
    <!-- Hero: Map / Grid with spot markers -->
    <div class="map-hero">
      <div v-if="shapeData" class="squares-block">
        <div class="squares-shape-wrapper">
          <div class="shape-container" :style="{ height: shapeHeight }">
            <svg v-if="streakPolyline" class="streak-line-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
              <polyline
                :points="streakPolyline"
                fill="none"
                stroke="white"
                stroke-width="2"
                vector-effect="non-scaling-stroke"
              />
            </svg>
            <span
              v-for="(cell, idx) in shapeCells"
              :key="idx"
              class="shape-square"
              :class="cell.classes"
              :style="{ left: cell.x + '%', top: cell.y + '%' }"
            >
              <span v-if="cell.correctedDot" class="corrected-dot" />
            </span>
            <!-- Start spot marker on map -->
            <div
              class="map-spot-marker start-marker"
              :class="{ 'has-spot': !!props.startSpotImage }"
              :style="{ left: originPos.x + '%', top: originPos.y + '%' }"
            >
              <img v-if="props.startSpotImage" :src="props.startSpotImage" class="map-spot-image" alt="" />
              <GamIcon v-else-if="props.startSpotIcon" :name="props.startSpotIcon" size="12px" class="map-spot-icon" />
              <span v-else class="map-spot-empty" />
              <span v-if="startSpotName" class="map-spot-name">{{ startSpotName }}</span>
            </div>
            <!-- Reveal spot marker on map -->
            <div
              v-if="revealPos"
              class="map-spot-marker reveal-marker"
              :class="{ 'has-spot': !!props.revealSpotImage }"
              :style="{ left: revealPos.x + '%', top: revealPos.y + '%' }"
            >
              <img v-if="props.revealSpotImage" :src="props.revealSpotImage" class="map-spot-image" alt="" />
              <GamIcon
                v-else-if="props.revealSpotIcon"
                :name="props.revealSpotIcon"
                size="12px"
                class="map-spot-icon"
              />
              <span v-else class="map-spot-full" />
              <span v-if="revealSpotName" class="map-spot-name">{{ revealSpotName }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Puzzle grid (map disabled) -->
      <div v-else class="squares-block">
        <div class="reveal-grid">
          <div v-for="(cell, idx) in gridCells" :key="idx" class="reveal-grid-square" :class="cell.cls">
            <span v-if="cell.correctedDot" class="corrected-dot" />
          </div>
        </div>
      </div>
    </div>

    <!-- Timeline bar -->
    <div class="route-timeline-block">
      <span class="route-time-above">
        <img :src="puzzleIcon" class="route-time-icon" alt="" />
        {{ solvingPercentage }}
      </span>
      <span class="route-timeline" :style="{ background: timelineGradient }" />
    </div>

    <!-- Row 1: Field + Success | Accuracy -->
    <div class="stats-grid">
      <div class="stat-cell stat-cell-success">
        <div class="stat-split">
          <span class="stat-split-field">{{ ratingBandLetter }}</span>
          <span class="stat-split-divider" />
          <div class="stat-split-right">
            <span class="stat-value-hero" :class="{ 'is-perfect': isSuccessPerfect }">
              {{ successPercentage }}
            </span>
          </div>
        </div>
        <!-- prettier-ignore -->
        <span
          v-if="!props.isHistory && successDelta !== 0"
          class="stat-cell-delta"
          :class="successDelta > 0 ? 'delta-up' : 'delta-down'"
        >{{ successDelta > 0 ? '▲' : '▼' }}{{ Math.abs(successDelta) }}%</span>
      </div>

      <div class="stat-cell">
        <span class="stat-cell-label">{{ localize('walk.reveal.accuracy') }}</span>
        <span class="stat-cell-value" :class="{ 'is-perfect': isAccuracyPerfect }">{{ accuracyPercentage }}</span>
        <span class="stat-cell-sub">{{ accuracyFraction }}</span>
      </div>

      <!-- Row 2: Solved/Wrong/NotSubmitted + Streak -->
      <div class="stat-cell">
        <span class="stat-cell-counts">
          <span class="stat-count-num solved">{{ totalSolvedCount }}</span>
          <span class="recap-square solved" />
          <span class="stat-count-num wrong">{{ totalWrongCount }}</span>
          <span class="recap-square wrong" />
          <span v-if="totalNotSubmittedCount > 0" class="stat-count-num not-submitted">
            {{ totalNotSubmittedCount }}
          </span>
          <span v-if="totalNotSubmittedCount > 0" class="recap-square not-submitted" />
        </span>
        <span v-if="totalCorrectedCount > 0" class="stat-cell-sub">{{ totalCorrectedCount }} corrected</span>
      </div>

      <div class="stat-cell" :class="{ 'is-record': !props.isHistory && isNewRecord }">
        <span class="stat-cell-label">{{ localize('walk.reveal.streak') }}</span>
        <span class="stat-cell-counts">
          <span class="recap-square trace-clean" />
          <span class="stat-cell-value streak-value">
            {{ streakLength }}
          </span>
        </span>
        <!-- prettier-ignore -->
        <span
          v-if="!props.isHistory && streakDelta !== 0"
          class="stat-cell-delta"
          :class="streakDelta > 0 ? 'delta-up' : 'delta-down'"
        >{{ streakDelta > 0 ? '▲' : '▼' }}{{ Math.abs(streakDelta) }}</span>
      </div>
    </div>

    <!-- Row 3: Distance + Time (full width) -->
    <div class="stat-row-wide">
      <div class="stat-wide-half">
        <span class="stat-cell-label">{{ localize('walk.reveal.distance') }}</span>
        <span class="stat-cell-value stat-small-value">{{ totalDistance || '-' }}</span>
      </div>
      <span class="stat-wide-divider" />
      <div class="stat-wide-half">
        <span class="stat-cell-label">{{ localize('walk.reveal.time') }}</span>
        <span class="stat-cell-value stat-small-value">{{ totalDuration }}</span>
      </div>
    </div>

    <!-- Session meta -->
    <div class="session-meta">
      <span class="meta-detail">{{ scoreMetaDetail }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import type { SessionSummaryResponse } from '@/core/network/api/walk/walk.api';
import { useWalkStore } from '@/stores/walk.store';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { computed } from 'vue';
import { ratingBandRange, ratingToBand } from '../utils/walk-rating-band';

const puzzleIcon = new URL('../../../../ui/icons/puzzle-grid.svg', import.meta.url).href;

const props = defineProps<{
  summary: SessionSummaryResponse | null;
  /** Client-side walk duration (Start tap → Reveal tap). Overrides server total when present. */
  walkDurationMs?: number | null;
  showThirdSpot?: boolean;
  startSpotLabel?: string | null;
  revealSpotLabel?: string | null;
  startSpotImage?: string | null;
  revealSpotImage?: string | null;
  startSpotIcon?: GamIconName | null;
  revealSpotIcon?: GamIconName | null;
  isHistory?: boolean;
}>();

const roundResults = computed(() => props.summary?.rounds ?? []);

const startSpotName = computed(() => props.startSpotLabel ?? props.summary?.startSpotName ?? null);
const revealSpotName = computed(() => props.revealSpotLabel ?? props.summary?.revealSpotName ?? null);

// --- Shape visualization ---

function projectCoords(
  rounds: typeof roundResults.value,
  originLat: number,
  originLng: number,
  revealLat?: number | null,
  revealLng?: number | null,
) {
  const allLats: number[] = [originLat];
  const allLngs: number[] = [originLng];

  if (revealLat != null && revealLng != null) {
    allLats.push(revealLat);
    allLngs.push(revealLng);
  }

  for (const r of rounds) {
    if (r.lat != null && r.lng != null) {
      allLats.push(r.lat);
      allLngs.push(r.lng);
    }
  }

  const minLat = Math.min(...allLats);
  const maxLat = Math.max(...allLats);
  const minLng = Math.min(...allLngs);
  const maxLng = Math.max(...allLngs);

  const latRange = maxLat - minLat || 0.0001;
  const lngRange = maxLng - minLng || 0.0001;

  const padding = 0;
  const toX = (lng: number) => padding * 100 + ((lng - minLng) / lngRange) * (1 - 2 * padding) * 100;
  const toY = (lat: number) => padding * 100 + ((maxLat - lat) / latRange) * (1 - 2 * padding) * 100;

  return { toX, toY, aspect: latRange / lngRange };
}

const shapeData = computed(() => {
  const rounds = roundResults.value;
  const summary = props.summary;
  if (!summary) return null;

  const originLat = summary.originLat;
  const originLng = summary.originLng;
  if (originLat == null || originLng == null) return null;

  const { toX, toY, aspect } = projectCoords(rounds, originLat, originLng, summary.revealLat, summary.revealLng);
  return { toX, toY, aspect };
});

const shapeHeight = computed(() => {
  const data = shapeData.value;
  if (!data) return '80px';
  // Scale height based on lat/lng aspect ratio (containerWidth ~220px)
  const containerWidth = 220;
  const raw = data.aspect * containerWidth;
  const clamped = Math.max(30, Math.min(140, raw));
  return `${clamped}px`;
});

const originPos = computed(() => {
  const summary = props.summary;
  const data = shapeData.value;
  if (!data || !summary) return { x: 50, y: 50 };
  const x = Math.max(0, Math.min(100, data.toX(summary.originLng)));
  const y = Math.max(0, Math.min(100, data.toY(summary.originLat)));
  return { x, y };
});

const revealPos = computed(() => {
  const summary = props.summary;
  const data = shapeData.value;
  if (!data || !summary || summary.revealLat == null || summary.revealLng == null) return null;
  const x = Math.max(0, Math.min(100, data.toX(summary.revealLng)));
  const y = Math.max(0, Math.min(100, data.toY(summary.revealLat)));
  return { x, y };
});

const shapeCells = computed(() => {
  const rounds = roundResults.value;
  const data = shapeData.value;

  const cells: { classes: string[]; correctedDot: boolean; x: number; y: number }[] = [];

  for (let i = 0; i < rounds.length; i++) {
    const r = rounds[i];
    const cls: string[] = [];

    if (!r.submitted) cls.push('not-submitted');
    else if (r.isCorrect) cls.push('correct');
    else if (r.redWalkIsCorrect === true) cls.push('corrected');
    else cls.push('incorrect');

    const correctedDot = r.submitted && !r.isCorrect && r.redWalkIsCorrect === true;

    let x = 50;
    let y = 50;
    if (data && r.lat != null && r.lng != null) {
      x = data.toX(r.lng);
      y = data.toY(r.lat);
    } else if (!data) {
      // Fallback grid layout when no coordinates
      const cols = 10;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const totalRows = Math.ceil(rounds.length / cols);
      x = (col / (cols - 1)) * 80 + 10;
      y = totalRows > 1 ? (row / (totalRows - 1)) * 80 + 10 : 50;
    }

    cells.push({ classes: cls, correctedDot, x, y });
  }

  return cells;
});

// --- Grid cells (map disabled mode) ---

const gridCells = computed(() => {
  const rounds = roundResults.value;
  const cells: { cls: string; correctedDot: boolean }[] = [];

  for (const r of rounds) {
    if (!r.submitted) {
      cells.push({ cls: 'not-submitted', correctedDot: false });
    } else if (r.isCorrect) {
      cells.push({ cls: 'correct', correctedDot: false });
    } else if (r.redWalkIsCorrect === true) {
      cells.push({ cls: 'corrected', correctedDot: true });
    } else {
      cells.push({ cls: 'incorrect', correctedDot: false });
    }
  }

  return cells;
});

// --- Timeline gradient ---

const timelineGradient = computed(() => {
  const rounds = roundResults.value;
  const summary = props.summary;
  if (!rounds.length) return 'var(--color-white-30)';

  const breakColor = 'rgba(231, 76, 100, 0.2)';
  const segments: { color: string; ms: number; isBreak: boolean }[] = [];

  for (const r of rounds) {
    const thinkColor = r.sideToMove === 'b' ? 'var(--color-white)' : '#111';
    if (r.thinkMs > 0) segments.push({ color: thinkColor, ms: r.thinkMs, isBreak: false });
    if (r.breakMs > 0) segments.push({ color: breakColor, ms: r.breakMs, isBreak: true });
  }

  const roundsMs = segments.reduce((sum, s) => sum + s.ms, 0);
  const totalDurationMs = summary?.totalDurationMs ?? 0;
  const trailingMs = totalDurationMs > roundsMs ? totalDurationMs - roundsMs : 0;
  if (trailingMs > 0) segments.push({ color: breakColor, ms: trailingMs, isBreak: true });

  if (segments.length === 0) return 'var(--color-white-30)';

  const totalMs = segments.reduce((sum, s) => sum + s.ms, 0);
  if (totalMs === 0) return 'var(--color-white-30)';

  let acc = 0;
  const stops: string[] = [];
  for (const seg of segments) {
    const start = (acc / totalMs) * 100;
    acc += seg.ms;
    const end = (acc / totalMs) * 100;
    if (seg.isBreak) {
      stops.push(`${seg.color} ${start.toFixed(1)}% ${end.toFixed(1)}%`);
    } else {
      stops.push(`${seg.color} ${start.toFixed(1)}% ${end.toFixed(1)}%`);
    }
  }

  return `linear-gradient(to right, ${stops.join(', ')})`;
});

const streakPolyline = computed(() => {
  const cells = shapeCells.value;
  const rounds = roundResults.value;
  if (!rounds.length) return null;

  let bestStart = 0;
  let bestLen = 0;
  let curStart = 0;
  let curLen = 0;
  for (let i = 0; i < rounds.length; i++) {
    if (rounds[i].submitted && rounds[i].isCorrect) {
      if (curLen === 0) curStart = i;
      curLen++;
      if (curLen > bestLen) {
        bestLen = curLen;
        bestStart = curStart;
      }
    } else {
      curLen = 0;
    }
  }

  if (bestLen < 2) return null;

  const points = cells.slice(bestStart, bestStart + bestLen).map((c) => `${c.x},${c.y}`);
  return points.join(' ');
});

// --- Success: solved / 50 (fixed target) ---

const TOTAL_PUZZLES = 50;

const totalSolvedCount = computed(() => props.summary?.totalSolved ?? 0);

const totalWrongCount = computed(() => {
  if (!props.summary) return 0;
  return props.summary.rounds.filter((r) => r.submitted && r.isCorrect === false).length;
});

const totalNotSubmittedCount = computed(() => {
  if (!props.summary) return 0;
  return props.summary.rounds.filter((r) => !r.submitted).length;
});

const totalCorrectedCount = computed(() => {
  if (!props.summary) return 0;
  return props.summary.rounds.filter((r) => r.submitted && !r.isCorrect && r.redWalkIsCorrect === true).length;
});

const successPercentage = computed(() => {
  if (!props.summary) return '-';
  const pct = Math.round((props.summary.totalSolved / TOTAL_PUZZLES) * 100);
  return `${pct}%`;
});

const isSuccessPerfect = computed(() => {
  if (!props.summary) return false;
  return props.summary.totalSolved >= TOTAL_PUZZLES;
});

// --- Accuracy: solved / submitted ---

const accuracyPercentage = computed(() => {
  if (!props.summary) return '-';
  const total = props.summary.rounds.length;
  if (total === 0) return '-';
  const pct = Math.round((props.summary.totalSolved / total) * 100);
  return `${pct}%`;
});

const accuracyFraction = computed(() => {
  if (!props.summary) return '-';
  const total = props.summary.rounds.length;
  return `${props.summary.totalSolved}/${total}`;
});

const isAccuracyPerfect = computed(() => {
  if (!props.summary) return false;
  const total = props.summary.rounds.length;
  return total > 0 && props.summary.totalSolved === total;
});

// --- Streak ---

const streakLength = computed(() => props.summary?.longestStreak ?? 0);

// --- Record comparison ---

const walkStore = useWalkStore();

const sessionField = computed(() => {
  if (!props.summary) return '';
  const speedKmh = Math.round(props.summary.breakEvenSpeedMps * 3.6);
  const timeSec = Math.round(props.summary.timeBankMs / 1000);
  const isVariant = props.summary.timeBankMs !== 1000;
  const band = ratingToBand(props.summary.puzzleRating);
  return isVariant ? `${speedKmh}${band} | ${timeSec}s` : `${speedKmh}${band}`;
});

/** Compare against other sessions only — never the current walk. */
const currentSessionId = computed(() => props.summary?.sessionId ?? null);

const existingRecord = computed(() => {
  const field = sessionField.value;
  const sessionId = currentSessionId.value;
  if (!field) return null;
  return walkStore.streakRecords.find((r) => r.field === field && r.sessionId !== sessionId) ?? null;
});

const isNewRecord = computed(() => {
  const len = streakLength.value;
  if (len <= 0) return false;
  const rec = existingRecord.value;
  if (!rec) return true;
  return len > rec.streakLength;
});

const streakDelta = computed(() => {
  const rec = existingRecord.value;
  if (!rec) return 0;
  return streakLength.value - rec.streakLength;
});

// --- Success delta vs best other session ---

const successDelta = computed(() => {
  const field = sessionField.value;
  const sessionId = currentSessionId.value;
  if (!field) return 0;
  const stats = walkStore.fieldStats.find((f) => f.field === field);
  if (!stats || !stats.topSessions.length) return 0;
  const bestOther = stats.topSessions.find((s) => s.sessionId !== sessionId);
  if (!bestOther) return 0;
  const currentPct = Math.round((totalSolvedCount.value / TOTAL_PUZZLES) * 100);
  return currentPct - bestOther.successPct;
});

// --- Footer settings ---

const ratingBandLetter = computed(() => {
  if (!props.summary) return '-';
  const speedKmh = Math.round(props.summary.breakEvenSpeedMps * 3.6);
  return `${ratingToBand(props.summary.puzzleRating).toUpperCase()}${speedKmh}`;
});

const scoreMetaDetail = computed(() => {
  if (!props.summary) return '';
  const speedKmh = Math.round(props.summary.breakEvenSpeedMps * 3.6);
  return `${ratingBandRange(props.summary.puzzleRating)} · ${speedKmh}km/h`;
});

const totalDuration = computed(() => {
  if (!props.summary?.totalDurationMs) return '-';
  return formatDuration(props.summary.totalDurationMs);
});

const totalDistance = computed(() => {
  if (!props.summary?.totalDistanceM) return '';
  const meters = props.summary.totalDistanceM;
  const km = meters / 1000;
  return km < 1 ? `${Math.round(meters)}m` : `${km.toFixed(1)}km`;
});

const solvingPercentage = computed(() => {
  const s = props.summary;
  if (!s || !s.totalDurationMs || !s.totalPuzzleTimeMs) return '0%';
  const fullDurationMs = (s.warmUpMs ?? 0) + s.totalDurationMs;
  if (fullDurationMs <= 0) return '0%';
  return Math.round((s.totalPuzzleTimeMs / fullDurationMs) * 100) + '%';
});

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec}s`;
  return `${min}m ${sec}s`;
}
</script>

<style lang="scss" scoped>
.reveal-content {
  padding: 1.25rem;
  padding-bottom: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

/* ── Map hero ─────────────────────── */

.map-hero {
  width: 100%;
  display: flex;
  justify-content: center;
}

.squares-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.squares-shape-wrapper {
  width: 100%;
  max-width: 260px;
  align-self: center;
  overflow: visible;
}

.shape-container {
  position: relative;
  width: 100%;
  min-height: 100px;
}

.streak-line-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
}

.shape-square {
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &.correct {
    background: var(--color-correct);
  }

  &.incorrect {
    background: var(--color-incorrect);
  }

  &.corrected {
    background: var(--color-incorrect);
  }

  &.not-submitted {
    background: transparent;
    box-shadow: inset 0 0 0 1.5px rgba(255, 255, 255, 0.25);
  }
}

.corrected-dot {
  width: 40%;
  height: 40%;
  border-radius: 50%;
  background: var(--color-correct);
}

/* ── Map spot markers ─────────────── */

.map-spot-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  pointer-events: none;
  width: 14px;
  height: 14px;
  border: none;
  background: none;

  &.has-spot {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.4);
    overflow: hidden;
  }
}

.map-spot-name {
  position: absolute;
  top: calc(100% + 3px);
  left: 50%;
  transform: translateX(-50%);
  font-size: 9px;
  font-weight: 500;
  color: var(--color-white-70);
  white-space: nowrap;
  max-width: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.map-spot-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.map-spot-icon {
  color: var(--color-white);
}

.map-spot-empty {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: none;
  box-shadow: inset 0 0 0 2px #ffffff;
}

.map-spot-full {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: #ffffff;
}

/* ── Puzzle grid (no-map mode) ────── */

.reveal-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 3px;
  width: 100%;
  max-width: 220px;
}

.reveal-grid-square {
  aspect-ratio: 1;
  border-radius: 2px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &.correct {
    background: var(--color-correct);
  }

  &.incorrect {
    background: var(--color-incorrect);
  }

  &.corrected {
    background: var(--color-incorrect);
  }

  &.not-submitted {
    background: transparent;
    box-shadow: inset 0 0 0 1.5px rgba(255, 255, 255, 0.25);
  }
}

/* ── Timeline bar ─────────────────── */

.route-timeline-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.route-time-above {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: var(--color-white-40);
}

.route-time-icon {
  width: 11px;
  height: 11px;
  filter: brightness(0) invert(1);
  opacity: 0.4;
}

.route-timeline {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  box-shadow:
    inset 0 0 0 0.5px rgba(255, 255, 255, 0.15),
    0 0 6px rgba(255, 255, 255, 0.08);
}

/* ── 2×2 Stats grid ───────────────── */

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;
  max-width: 300px;
}

.stat-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  gap: 2px;
  position: relative;
}

.stat-cell-success {
  padding: 0;
}

.stat-split {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.stat-split-field {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 800;
  color: var(--color-white);
  padding: 0 12px;
  flex-shrink: 0;
}

.stat-split-divider {
  width: 1px;
  align-self: stretch;
  margin: 10px 0;
  background: var(--color-white-10);
  flex-shrink: 0;
}

.stat-split-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 2px;
  padding: 8px 8px;
}

.stat-value-hero {
  font-size: 32px;
  font-weight: 800;
  color: var(--color-white);
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.stat-cell-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-white-40);
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-cell-value {
  font-size: 22px;
  font-weight: 700;
  color: var(--color-white);
  line-height: 1.2;
  font-variant-numeric: tabular-nums;

  &.is-perfect {
    color: var(--color-correct);
    font-weight: 700;
  }
}

.stat-cell-sub {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-white-40);
  font-variant-numeric: tabular-nums;
}

.stat-cell-counts {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-count-num {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;

  &.solved {
    color: var(--color-correct);
  }

  &.wrong {
    color: var(--color-incorrect);
  }

  &.not-submitted {
    color: var(--color-white-30);
  }
}

.stat-small-value {
  font-size: 18px;
}

.stat-cell-delta {
  font-size: 11px;
  font-weight: 700;
  position: absolute;
  top: 6px;
  right: 8px;

  &.delta-up {
    color: var(--color-correct);
  }

  &.delta-down {
    color: var(--color-incorrect);
  }
}

.recap-square {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;

  &.trace-clean {
    background: var(--color-correct);
  }

  &.solved {
    background: var(--color-correct);
  }

  &.wrong {
    background: var(--color-incorrect);
  }

  &.not-submitted {
    background: transparent;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
  }
}

/* ── Distance + Time row ─────────── */

.stat-row-wide {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 300px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  padding: 10px 0;
}

.stat-wide-half {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stat-wide-divider {
  width: 1px;
  height: 28px;
  background: var(--color-white-10);
  flex-shrink: 0;
}

/* ── Session meta chips ───────────── */

.session-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.meta-detail {
  font-size: 15px;
  font-weight: 500;
  color: var(--color-white-50);
}
</style>
